import type { PulseQuestion } from "@shared/schema";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";

interface SurveyPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | null;
  questions: PulseQuestion[];
}

export function SurveyPreviewDialog({
  open,
  onOpenChange,
  title,
  description,
  questions,
}: SurveyPreviewDialogProps) {
  const [previewAnswers, setPreviewAnswers] = useState<
    Record<string, { answerText?: string; answerValue?: number }>
  >({});

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setPreviewAnswers({});
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="text-xl">{title}</DialogTitle>
            <Badge variant="secondary">Pré-visualização</Badge>
          </div>
          {description && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{description}</p>
          )}
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {questions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhuma pergunta adicionada ainda.
            </p>
          ) : (
            questions.map((question, index) => {
              const questionOptions = (question.options as Array<{ label: string }>) || [];
              const qType = question.questionType === "text" ? "short_text" : question.questionType;

              return (
                <Card key={question.id} data-testid={`preview-question-${index}`}>
                  <CardContent className="pt-4 space-y-3">
                    <div>
                      <Label className="text-sm font-medium">
                        {index + 1}. {question.questionText}
                        {question.required && (
                          <span className="text-[hsl(var(--alert-red))] ml-1">*</span>
                        )}
                      </Label>
                      {question.description && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                          {question.description}
                        </p>
                      )}
                    </div>

                    {qType === "short_text" ? (
                      <Input
                        placeholder="Digite sua resposta..."
                        value={previewAnswers[question.id]?.answerText ?? ""}
                        onChange={(e) =>
                          setPreviewAnswers((prev) => ({
                            ...prev,
                            [question.id]: { answerText: e.target.value },
                          }))
                        }
                        data-testid={`preview-input-${index}`}
                      />
                    ) : qType === "long_text" ? (
                      <Textarea
                        placeholder="Digite sua resposta..."
                        value={previewAnswers[question.id]?.answerText ?? ""}
                        onChange={(e) =>
                          setPreviewAnswers((prev) => ({
                            ...prev,
                            [question.id]: { answerText: e.target.value },
                          }))
                        }
                        className="min-h-[100px]"
                        data-testid={`preview-input-${index}`}
                      />
                    ) : qType === "single_choice" && questionOptions.length > 0 ? (
                      <RadioGroup
                        value={previewAnswers[question.id]?.answerText ?? ""}
                        onValueChange={(value) =>
                          setPreviewAnswers((prev) => ({
                            ...prev,
                            [question.id]: { answerText: value },
                          }))
                        }
                        className="space-y-2"
                        data-testid={`preview-input-${index}`}
                      >
                        {questionOptions.map((opt, optIdx) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: survey options may have duplicate labels from user input
                          <div key={optIdx} className="flex items-center space-x-2">
                            <RadioGroupItem
                              value={opt.label}
                              id={`preview-${question.id}-opt-${optIdx}`}
                              data-testid={`preview-radio-${index}-${optIdx}`}
                            />
                            <Label
                              htmlFor={`preview-${question.id}-opt-${optIdx}`}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {opt.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    ) : qType === "scale" ? (
                      <div className="space-y-4 p-4 rounded-md border bg-muted/30">
                        <div className="px-2 relative">
                          <Slider
                            min={question.scaleMin || 1}
                            max={question.scaleMax || 5}
                            step={1}
                            value={[
                              previewAnswers[question.id]?.answerValue || question.scaleMin || 1,
                            ]}
                            onValueChange={(value) =>
                              setPreviewAnswers((prev) => ({
                                ...prev,
                                [question.id]: { answerValue: value[0] },
                              }))
                            }
                            className="cursor-pointer"
                          />
                          <div className="flex justify-between px-1 mt-2">
                            {Array.from(
                              {
                                length: (question.scaleMax || 5) - (question.scaleMin || 1) + 1,
                              },
                              (_, i) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: scale tick marks are static and never reordered
                                <div key={i} className="flex flex-col items-center">
                                  <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                                  <span className="text-xs text-muted-foreground/60 mt-1">
                                    {(question.scaleMin || 1) + i}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm font-medium px-2">
                          <span className="text-muted-foreground">
                            {question.scaleMinLabel || question.scaleMin}
                          </span>
                          <span className="text-muted-foreground">
                            {question.scaleMaxLabel || question.scaleMax}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
